/**
 * Sessão do painel — escopo (ABA × PRODUTO).
 *
 * O app da creche (`/app`) e o painel SaaS (`/saas`) são produtos diferentes,
 * com usuários e tenants diferentes, servidos pela MESMA origem. Enquanto os
 * dois dividiram o slot `token` do localStorage, logar em um deles trocava a
 * sessão do outro por baixo dos panos: a tela continuava mostrando os dados já
 * carregados, mas as requisições saíam como outro usuário, com outro
 * `x-tenant-id` — origem do falso "Aluno não encontrado" e do risco real de
 * gravar dado na creche errada.
 *
 * Regras desta camada:
 *  1. A fonte de verdade da aba é o **sessionStorage**, que é isolado por aba.
 *     Nenhuma outra aba consegue trocar o token que esta aba usa.
 *  2. O localStorage guarda apenas a ÚLTIMA sessão de cada produto, como
 *     semente: uma aba nova (ou um link aberto em nova aba) adota essa semente
 *     na primeira leitura e a partir daí segue sozinha.
 *  3. Tudo é separado por área (`app` | `saas`), derivada da rota. A mesma aba
 *     pode ter as duas sessões sem que uma atropele a outra.
 *  4. Logout explícito marca a aba como desconectada, para ela não readotar a
 *     semente de outro login.
 */

export type AreaSessao = 'app' | 'saas';

type CampoSessao = 'token' | 'creche' | 'lgpd';

const PREFIXO_ABA = 'gc.sessao';
const PREFIXO_SEMENTE = 'gc.ultima-sessao';

/** Chaves da fase em que `/app` e `/saas` dividiam a mesma sessão. */
const LEGADO: Record<CampoSessao, string> = {
  token: 'token',
  lgpd: 'lgpd_pendente',
  creche: 'gestao-creches-creche-selecionada',
};

function chaveAba(area: AreaSessao, campo: CampoSessao) {
  return `${PREFIXO_ABA}.${area}.${campo}`;
}

function chaveSemente(area: AreaSessao, campo: CampoSessao) {
  return `${PREFIXO_SEMENTE}.${area}.${campo}`;
}

function chaveDesconectada(area: AreaSessao) {
  return `${PREFIXO_ABA}.${area}.desconectada`;
}

// Storage pode lançar (modo privativo, cota, iframe bloqueado). Sessão nunca
// deve derrubar a tela por causa disso.
function ler(store: Storage | undefined, chave: string): string | null {
  try {
    return store?.getItem(chave) ?? null;
  } catch {
    return null;
  }
}

function gravar(store: Storage | undefined, chave: string, valor: string) {
  try {
    store?.setItem(chave, valor);
  } catch {
    /* noop */
  }
}

function remover(store: Storage | undefined, chave: string) {
  try {
    store?.removeItem(chave);
  } catch {
    /* noop */
  }
}

const abaStore = typeof window !== 'undefined' ? window.sessionStorage : undefined;
const semeadoStore = typeof window !== 'undefined' ? window.localStorage : undefined;

function decodificarPayload(token?: string | null): any | null {
  if (!token) return null;
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch {
    return null;
  }
}

function ehAdminDeSistema(payload: any): boolean {
  const roles = Array.isArray(payload?.roles) ? payload.roles : [];
  const normalizados = roles.map((r: unknown) => String(r || '').toLowerCase());
  const tipo = String(payload?.tipo_usuario || '').toLowerCase();
  const admin = ['super_admin', 'super_admin_assistente', 'saas_admin', 'saas_admin_assistente'];
  return normalizados.some((r: string) => admin.includes(r)) || admin.includes(tipo);
}

/** Área do produto a partir da rota. Fora do browser, assume o app da creche. */
export function areaDaRota(pathname?: string): AreaSessao {
  const caminho = pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '');
  return String(caminho).startsWith('/saas') ? 'saas' : 'app';
}

/**
 * Migra a sessão do formato antigo (slot único) para o slot da área a que ela
 * pertence, deduzida do papel de quem está logado. Roda uma vez: as chaves
 * legadas são apagadas em seguida, então quem já está logado não é deslogado
 * pelo deploy.
 */
let legadoMigrado = false;
function migrarSessaoLegada() {
  if (legadoMigrado) return;
  legadoMigrado = true;

  const tokenLegado = ler(semeadoStore, LEGADO.token);
  if (!tokenLegado) {
    // Sem token legado não há sessão a migrar; limpa restos soltos.
    remover(semeadoStore, LEGADO.lgpd);
    return;
  }

  const area: AreaSessao = ehAdminDeSistema(decodificarPayload(tokenLegado)) ? 'saas' : 'app';
  const campos: Array<[CampoSessao, string | null]> = [
    ['token', tokenLegado],
    ['lgpd', ler(semeadoStore, LEGADO.lgpd)],
    ['creche', ler(semeadoStore, LEGADO.creche)],
  ];

  for (const [campo, valor] of campos) {
    if (valor === null) continue;
    gravar(semeadoStore, chaveSemente(area, campo), valor);
    gravar(abaStore, chaveAba(area, campo), valor);
  }

  remover(semeadoStore, LEGADO.token);
  remover(semeadoStore, LEGADO.lgpd);
  remover(semeadoStore, LEGADO.creche);
}

function lerCampo(campo: CampoSessao, area: AreaSessao): string | null {
  migrarSessaoLegada();

  const naAba = ler(abaStore, chaveAba(area, campo));
  if (naAba !== null) return naAba;

  // Aba que deslogou explicitamente não readota a semente de outro login.
  if (ler(abaStore, chaveDesconectada(area))) return null;

  const semente = ler(semeadoStore, chaveSemente(area, campo));
  if (semente !== null) gravar(abaStore, chaveAba(area, campo), semente);
  return semente;
}

function gravarCampo(campo: CampoSessao, valor: string | null, area: AreaSessao) {
  migrarSessaoLegada();

  if (valor === null) {
    remover(abaStore, chaveAba(area, campo));
    remover(semeadoStore, chaveSemente(area, campo));
    return;
  }
  gravar(abaStore, chaveAba(area, campo), valor);
  gravar(semeadoStore, chaveSemente(area, campo), valor);
}

/** Token JWT desta aba para o produto informado (padrão: o produto da rota). */
export function lerToken(area: AreaSessao = areaDaRota()): string | null {
  return lerCampo('token', area);
}

/** Registra a sessão recém-autenticada nesta aba e como semente de novas abas. */
export function iniciarSessao(token: string, area: AreaSessao = areaDaRota()) {
  remover(abaStore, chaveDesconectada(area));
  gravarCampo('token', token, area);
}

/** Encerra a sessão do produto NESTA aba (as outras abas seguem intactas). */
export function encerrarSessao(area: AreaSessao = areaDaRota()) {
  gravarCampo('token', null, area);
  gravarCampo('lgpd', null, area);
  gravarCampo('creche', null, area);
  gravar(abaStore, chaveDesconectada(area), '1');
}

export function lerLgpdPendente(area: AreaSessao = areaDaRota()): string | null {
  return lerCampo('lgpd', area);
}

export function gravarLgpdPendente(valor: string | null, area: AreaSessao = areaDaRota()) {
  gravarCampo('lgpd', valor, area);
}

/** Creche escolhida no seletor (só admin de sistema tem seleção própria). */
export function lerCrecheSelecionada(area: AreaSessao = areaDaRota()): number {
  const raw = lerCampo('creche', area);
  return raw ? Number(raw) || 0 : 0;
}

export function gravarCrecheSelecionada(idCreche: number | null, area: AreaSessao = areaDaRota()) {
  gravarCampo('creche', idCreche && idCreche > 0 ? String(idCreche) : null, area);
}
